import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {
  EntityType,
  InternalNotificationKey,
  InternalNotificationMessageCode,
  RelatedObjectType,
  SubjectObjectRelationDto,
  SubjectRecordClassification,
  SubjectRecordCreateOrUpdateDto,
  SubjectRecordDto,
  SubjectRecordWithRelationsDto
} from '|api/commons';
import {ApiSubjectRecordElasticService} from '|api/subject-register';
import {
  AbstractObjectDetailService,
  constructSubjectName,
  CreateOrUpdateSubjectAction,
  DocumentDetailService,
  ELASTIC_RELOAD_DELAY,
  GenericSearchService,
  IczSimpleTableDataSource,
  SUBJECT_CREATE_OR_UPDATE_WITH_DUPLICATE_RESOLVE,
  SUBJECT_REGISTER_MICROSERVICE_ROOT,
  SubjectOperationResult,
  SubjectOperationType,
  SubjectsService,
  SubjectsTableComponent,
  SubjectsTableDataType,
  SubjectsToolbarButtonsComponent,
  SubjectTableColumnSet,
  SubjectToolbarView,
} from '|shared';
import {LoadingIndicatorService, WaitingDirective} from '@icz/angular-essentials';
import {IczTableDataSource} from '@icz/angular-table';
import {delay, take} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AsyncPipe} from '@angular/common';
import {SubjectToastService, SubjectToastType, UserSettingsService, WebSocketNotificationsService} from '|ui-essl-core';


@Component({
  selector: 'icz-object-detail-subjects',
  templateUrl: './object-detail-subjects.component.html',
  styleUrls: ['./object-detail-subjects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SubjectsTableComponent, WaitingDirective, SubjectsToolbarButtonsComponent, AsyncPipe]
})
export class ObjectDetailSubjectsComponent implements OnInit {

  protected loadingIndicatorService = inject(LoadingIndicatorService);
  protected userSettingsService = inject(UserSettingsService);
  protected abstractObjectDetailService = inject(AbstractObjectDetailService);
  private wsNotificationService = inject(WebSocketNotificationsService);
  private subjectCreateOrUpdateWithDuplicateResolveService = inject(SUBJECT_CREATE_OR_UPDATE_WITH_DUPLICATE_RESOLVE);
  private subjectsService = inject(SubjectsService);
  private subjectToastService = inject(SubjectToastService);
  private genericSearchService = inject(GenericSearchService);
  private destroyRef = inject(DestroyRef);

  @ViewChild(SubjectsToolbarButtonsComponent)
  toolbar!: SubjectsToolbarButtonsComponent;

  relatedObjectType = this.abstractObjectDetailService instanceof DocumentDetailService ? RelatedObjectType.DOCUMENT : RelatedObjectType.FILE;
  parentEntityType = this.abstractObjectDetailService instanceof DocumentDetailService ? EntityType.DOCUMENT : EntityType.FILE;

  dataSource!: IczTableDataSource<SubjectsTableDataType>;
  selectedRows: SubjectRecordDto[] = [];

  // enums for template
  readonly SubjectRecordClassification = SubjectRecordClassification;
  readonly SubjectTableColumnSet = SubjectTableColumnSet;
  readonly SubjectToolbarView = SubjectToolbarView;
  readonly EntityType = EntityType;

  setDataSource() {
    this.abstractObjectDetailService.objectId$.pipe(
      take(1),
    ).subscribe(objectId => {
      this.dataSource = new IczSimpleTableDataSource<SubjectsTableDataType>(this.genericSearchService, {
        url: ApiSubjectRecordElasticService.SubjectRecordElasticSearchByRelatedObjectPath,
        microServiceRoot: SUBJECT_REGISTER_MICROSERVICE_ROOT,
        staticPathParams: {relatedObjectId: objectId.toString(), relatedObjectType: this.relatedObjectType!}
      });
    });
  }

  onRowClick(rows: SubjectRecordDto[]) {
    this.selectedRows = rows;
  }

  private onUpdateSuccess(subject: SubjectRecordDto | SubjectRecordCreateOrUpdateDto) {
    const name = constructSubjectName(subject);
    this.subjectToastService.dispatchSubjectInfoToast(SubjectToastType.SUBJECT_UPDATE_SUCCESS, {[InternalNotificationKey.SUBJECT_NAME]: name});
    this.setDataSource();
  }

  private updateSubject(subjectToUpdate: SubjectRecordCreateOrUpdateDto, relation: SubjectObjectRelationDto) {
    subjectToUpdate.objectRelations = undefined;

    this.loadingIndicatorService.doLoading(
      this.subjectCreateOrUpdateWithDuplicateResolveService.createOrUpdateSubjectWithDuplicateResolve(
        CreateOrUpdateSubjectAction.CREATE_OR_UPDATE_SUBJECT,
        {subject: subjectToUpdate, createRelation: relation},
        this.abstractObjectDetailService.objectId
      ).pipe(
        delay(ELASTIC_RELOAD_DELAY)
      ),
      this
    ).subscribe(createAttemptResult => {
      if (!createAttemptResult) {
        return; // no result means closing the duplicate dialog without any action
      }
      else {
        this.onUpdateSuccess(createAttemptResult);
      }
    });
  }

  toolbarOperationCompleted(result: SubjectOperationResult) {
    if (result.outputSubject && result.operation === SubjectOperationType.IDENTIFY && this.selectedRows?.[0]) {
      const relation = (result.inputSubject as SubjectRecordWithRelationsDto).objectRelations!.find(
        or => or.relatedObjectId === this.abstractObjectDetailService.objectId
      )!;
      const subjectToUpdate: SubjectRecordCreateOrUpdateDto = {
        ...this.subjectsService.enrichExistingSubjectWithIdentified(this.selectedRows[0], result.outputSubject).enrichedSubject,
        valueCorrectionMode: result.additionalOperationResult?.valueCorrectionMode};
      this.updateSubject(subjectToUpdate, relation);
    }

    this.setDataSource();
  }

  ngOnInit() {
    this.setDataSource();
    this.wsNotificationService.getMessageListener$(InternalNotificationMessageCode.SETTLEMENT_DOCUMENT_SUCCESS)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
        this.dataSource.reload(true);
      }
    );
  }


}
